import { useEffect, useState } from 'react';
import { QuestionFactoryPanel } from './QuestionFactoryPanel';
import {
  loadModerationReports, moderateReport, type ModerationAction, type ModerationReport
} from './lib/moderation';

const reasonLabel = (reason: string): string => ({
  harassment: 'Harassment', sexual_content: 'Sexual content', hate_or_abuse: 'Hate or abuse',
  spam: 'Spam', unsafe_behavior: 'Unsafe behavior', other: 'Other'
}[reason] ?? reason);

export function ModerationPanel({ initData, onHome }: { initData: string; onHome: () => void }) {
  const [view, setView] = useState<'safety'|'factory'>('safety');
  const [reports, setReports] = useState<ModerationReport[] | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refresh = (): void => {
    setError(null); setReports(null);
    void loadModerationReports(initData).then(setReports).catch(() => setError('Could not load the moderation queue.'));
  };
  useEffect(refresh, [initData]);

  const act = async (report: ModerationReport, action: ModerationAction): Promise<void> => {
    if (action === 'ban' && !window.confirm(`Permanently ban ${report.target.firstName}?`)) return;
    setBusyId(report.id); setError(null);
    try {
      await moderateReport(initData, report, action);
      setReports((current) => current?.filter((item) => item.id !== report.id) ?? []);
    } catch { setError("Couldn't save that moderation action yet."); }
    finally { setBusyId(null); }
  };

  if (view === 'factory') return <QuestionFactoryPanel initData={initData} onSafety={() => setView('safety')} onHome={onHome} />;

  return <main className="app-shell moderation-screen">
    <header className="game-header"><div className="brand-mark">UTAGE</div><button className="text-action moderation-home" type="button" onClick={onHome}>HOME</button></header>
    <section className="moderation-heading"><p className="eyebrow">MODERATOR ONLY</p><h1>SAFETY QUEUE</h1><p>Private reports awaiting a decision. Every action is authenticated and audited.</p><button className="secondary-action moderation-factory-entry" type="button" onClick={() => setView('factory')}>QUESTION FACTORY</button></section>
    {reports === null && !error ? <p className="quiet-copy">Loading reports…</p> : null}
    {reports?.length === 0 ? <section className="moderation-clear"><div className="collective-dots"><span className="presence-dot" /></div><h2>QUEUE CLEAR.</h2><button className="secondary-wide" onClick={refresh}>REFRESH</button></section> : null}
    <div className="moderation-list">{reports?.map((report) => {
      const busy = busyId === report.id;
      return <article className="moderation-card" key={report.id}>
        <div className="moderation-card-title"><div><span>{report.contextType.toUpperCase()} REPORT</span><h2>{report.target.firstName}</h2></div><strong>{reasonLabel(report.reason)}</strong></div>
        <p>{new Date(report.createdAt).toLocaleString()} · {report.target.accountStatus.toUpperCase()}</p>
        <div className="moderation-flags"><span>Someone New: {report.target.matchmakingEnabled ? 'ON' : 'OFF'}</span><span>Challenges: {report.target.challengeEnabled ? 'ON' : 'OFF'}</span></div>
        <div className="moderation-actions">
          <button disabled={busy} onClick={() => void act(report, 'suspend_24h')}>SUSPEND 24H</button>
          <button disabled={busy} onClick={() => void act(report, 'suspend_7d')}>SUSPEND 7D</button>
          <button className="danger" disabled={busy} onClick={() => void act(report, 'ban')}>BAN</button>
          <button disabled={busy} onClick={() => void act(report, report.target.matchmakingEnabled ? 'disable_matchmaking' : 'enable_matchmaking')}>{report.target.matchmakingEnabled ? 'DISABLE' : 'ENABLE'} SOMEONE NEW</button>
          <button disabled={busy} onClick={() => void act(report, report.target.challengeEnabled ? 'disable_challenges' : 'enable_challenges')}>{report.target.challengeEnabled ? 'DISABLE' : 'ENABLE'} CHALLENGES</button>
          {report.target.accountStatus !== 'active' ? <button disabled={busy} onClick={() => void act(report, 'restore')}>RESTORE ACCOUNT</button> : null}
          <button disabled={busy} onClick={() => void act(report, 'resolve_report')}>RESOLVE — NO ACCOUNT ACTION</button>
          <button disabled={busy} onClick={() => void act(report, 'dismiss_report')}>DISMISS REPORT</button>
        </div>
        {busy ? <p className="status-line">SAVING VERIFIED ACTION…</p> : null}
      </article>;
    })}</div>
    {error ? <p className="inline-error" role="alert">{error} <button type="button" onClick={refresh}>TRY AGAIN</button></p> : null}
  </main>;
}
